import React, { useState } from 'react';
import { AlertCircle, ArrowLeft, Search, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { useFetch, Spinner } from '../lib/useFetch';
import { formazioneApi } from '../lib/api';
import { fmtDate } from '../lib/format';

const TitoliDaVerificarePage = () => {
  const [q, setQ] = useState('');
  const { data, loading } = useFetch(() => formazioneApi.titoliDaVerificare(), []);
  if (loading) return <Spinner />;

  const items = (data?.items || []).filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (r.workerName || '').toLowerCase().includes(s)
      || (r.companyName || '').toLowerCase().includes(s)
      || (r.id || '').toLowerCase().includes(s)
    );
  });

  const downloadCsv = () => {
    const header = ['ID sorgente', 'Lavoratore', 'Azienda', 'Rilascio', 'Scadenza', 'Durata', 'Job81 worker id', 'Job81 company id', 'Flaggato il'];
    const csv = [
      header.join(';'),
      ...items.map((r) => [
        r.id, r.workerName, r.companyName, r.dataRilascio || '', r.scadenza || '',
        r.durata || '', r.sourceRef?.job81_worker_id || '', r.sourceRef?.job81_company_id || '',
        r.flaggedAt || '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `titoli-da-verificare-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="space-y-5" data-testid="titoli-verificare-page">
      <div className="flex items-center gap-3">
        <Link to="/formazione" className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"><ArrowLeft size={16} /></Link>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">Titoli da verificare</h1>
          <p className="text-xs text-slate-500 mt-0.5">Record formazione senza titolo — in attesa di recupero dal portale sorgente. Non contabilizzati nelle KPI di Formazione.</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-800 leading-relaxed">
          <strong>Perché sono qui?</strong> Lo scraper JOB81 ha registrato per ogni riga la data di rilascio, la scadenza e il lavoratore ma il campo "titolo corso" era vuoto. Nessuna categoria è stata dedotta arbitrariamente. I dati originari sono <strong>preservati</strong> — quando il portale sorgente esporrà il titolo, ripartirà il recupero automatico e questi record confluiranno nella Formazione normale.
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-[240px]">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca lavoratore, azienda o ID..." className="pl-9 h-10 border-slate-200" data-testid="titoli-verificare-search" />
            </div>
            <div className="text-xs text-slate-500 whitespace-nowrap">
              {items.length} record • {data?.totale || 0} totali
            </div>
          </div>
          <button onClick={downloadCsv} className="px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 flex items-center gap-2" data-testid="titoli-verificare-export">
            <Download size={13} /> ESPORTA CSV
          </button>
        </div>

        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left bg-slate-100 rounded-l-xl px-4 py-3">ID sorgente</th>
                <th className="text-left bg-slate-100 px-4 py-3">Lavoratore</th>
                <th className="text-left bg-slate-100 px-4 py-3">Azienda</th>
                <th className="text-left bg-slate-100 px-4 py-3">Rilascio</th>
                <th className="text-left bg-slate-100 px-4 py-3">Scadenza</th>
                <th className="text-left bg-slate-100 rounded-r-xl px-4 py-3">Rif. JOB81</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`titoli-verificare-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{r.id}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{r.workerName}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{r.companyName}</td>
                  <td className="px-4 py-3 text-slate-700">{fmtDate(r.dataRilascio)}</td>
                  <td className="px-4 py-3 text-slate-700">{fmtDate(r.scadenza)}</td>
                  <td className="px-4 py-3 text-[10px] font-mono text-slate-400">
                    w:{r.sourceRef?.job81_worker_id || '—'} · c:{r.sourceRef?.job81_company_id || '—'}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-sm text-slate-400">Nessun record — tutti i titoli sono stati recuperati ✓</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TitoliDaVerificarePage;
